import React, { useState } from 'react';
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  message,
  Button,
  Space,
  Row,
  Col
} from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useMutation, useLazyQuery } from '@apollo/client';
import { useTranslation } from '../../hooks/useTranslation';
import { CREATE_TRANSACTION, CREATE_INSURANCE_CLAIM, UPDATE_INSURANCE_CLAIM } from '../../graphql/mutations';
import { GET_INSURANCE_CLAIMS_BY_APPOINTMENT, GET_INSURANCE_ELIGIBILITY_BY_CATEGORY, GET_APPOINTMENT, GET_INSURANCE_POLICIES_BY_USER } from '../../graphql/queries';
import i18n from '../../i18n';
import { toQfpayLang } from '../../utils/qfpay-lang';

const { TextArea } = Input;

const buildQfpayMetadata = (appointmentId: string) => {
  const frontendUrl = process.env.REACT_APP_FRONTEND_URL || 'http://localhost:3220';
  const qfpayLang = toQfpayLang(i18n.language);
  return {
    return_url: `${frontendUrl}/transactions/${appointmentId}/return`,
    failed_url: `${frontendUrl}/transactions/${appointmentId}/failed`,
    cancel_url: `${frontendUrl}/transactions/${appointmentId}/cancel`,
    ...(qfpayLang ? { lang: qfpayLang } : {}),
  };
};

interface CreateTransactionDialogProps {
  visible: boolean;
  onCancel: () => void;
  appointmentId: string;
  onSuccess?: (transaction: any) => void;
  onRefetch?: () => void;
}

const CreateTransactionDialog: React.FC<CreateTransactionDialogProps> = ({
  visible,
  onCancel,
  appointmentId,
  onSuccess,
  onRefetch
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [createTransaction, { loading: creating }] = useMutation(CREATE_TRANSACTION);
  const [createInsuranceClaim] = useMutation(CREATE_INSURANCE_CLAIM);
  const [updateInsuranceClaim] = useMutation(UPDATE_INSURANCE_CLAIM);
  const [checkEligibility] = useLazyQuery(GET_INSURANCE_ELIGIBILITY_BY_CATEGORY);
  const [getClaims] = useLazyQuery(GET_INSURANCE_CLAIMS_BY_APPOINTMENT);
  const [getAppointment] = useLazyQuery(GET_APPOINTMENT);
  const [getPoliciesByUser] = useLazyQuery(GET_INSURANCE_POLICIES_BY_USER);

  const proceedWithNormalTransaction = async (transactionData: any, values: any) => {
    try {
      // Add QFPay metadata if payment method is qfpay_checkout
      if (values.payment_method === 'qfpay_checkout') {
        transactionData.metadata = {
          qfpay: buildQfpayMetadata(appointmentId),
        };
      }
      
      const result = await createTransaction({
        variables: {
          createTransactionDto: transactionData
        }
      });
      
      message.success(t('appointments.transactions.transactionCreated'));
      form.resetFields();
      onRefetch?.();
      onSuccess?.(result.data.createTransaction);
      onCancel();
    } catch (err: any) {
      message.error(err?.message || t('appointments.transactions.transactionCreationFailed'));
    }
  };

  // Helper function to add claim items to an existing claim or create a new one
  // One claim per appointment
  const addClaimItems = async (policyId: string, newItems: any[]) => {
    try {
      // Validate new items
      const validNewItems = newItems.filter(item => 
        item && 
        item.name && 
        item.category && 
        item.amount && 
        item.approvedAmount
      );

      if (validNewItems.length === 0) {
        console.warn('No valid items to add to claim');
        return;
      }

      // Get existing claims for the appointment
      const claimsResult = await getClaims({
        variables: { appointmentId },
        fetchPolicy: 'network-only'
      });

      const claims = claimsResult.data?.insuranceClaimResultsByAppointment || [];
      
      // Find any active claim for this appointment (one claim per appointment)
      let existingClaim = claims.find((claim: any) => claim && claim.isActive && claim.id);

      if (existingClaim && existingClaim.id) {
        // Merge existing items with new items
        const existingItems = existingClaim.items || [];
        const validExistingItems = existingItems
          .filter((item: any) => item && item.name && item.category && item.amount && item.approvedAmount)
          .map((item: any) => ({
            name: String(item.name),
            category: String(item.category),
            amount: String(item.amount),
            approvedAmount: String(item.approvedAmount),
            transactionId: item.transactionId ? String(item.transactionId) : undefined
          }));

        const mergedItems = [
          ...validExistingItems,
          ...validNewItems.map(item => ({
            name: String(item.name),
            category: String(item.category),
            amount: String(item.amount),
            approvedAmount: String(item.approvedAmount),
            transactionId: item.transactionId ? String(item.transactionId) : undefined
          }))
        ];

        // Calculate total approved amount
        const totalApprovedAmount = mergedItems.reduce((sum, item) => sum + parseFloat(item.amount || '0'), 0);

        // Update the existing claim with merged items
        await updateInsuranceClaim({
          variables: {
            id: existingClaim.id,
            updateInsuranceClaimResultDto: {
              items: mergedItems,
              claimResultDetails: {
                approved_amount: totalApprovedAmount,
                rejected_amount: 0
              }
            }
          }
        });
      } else {
        // Create a new claim with items
        const itemsToCreate = validNewItems.map(item => ({
          name: String(item.name),
          category: String(item.category),
          amount: String(item.amount),
          approvedAmount: String(item.approvedAmount),
          transactionId: item.transactionId ? String(item.transactionId) : undefined
        }));

        await createInsuranceClaim({
          variables: {
            createInsuranceClaimResultDto: {
              name: `Claim for ${validNewItems[0]?.category || 'Transaction'}`,
              policyId: policyId,
              appointmentId: appointmentId,
              status: 'approved',
              claimResultDetails: {
                approved_amount: itemsToCreate.reduce((sum, item) => sum + parseFloat(item.amount || '0'), 0),
                rejected_amount: 0
              },
              items: itemsToCreate,
              isActive: true
            }
          }
        });
      }
    } catch (err: any) {
      console.error('Failed to add claim items:', err);
      throw err;
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      // Prepare transaction data
      const transactionData: any = {
        ...values,
        appointment_id: appointmentId,
        status: 'pending',
        type: 'pre_appointment',
        currency: 'HKD'
      };

      // Always attempt eligibility check if category is provided
      if (values.category) {
        let policyId: string | null = null;
        
        try {
          // First, try to get policyId from existing claims
          const claimsResult = await getClaims({
            variables: { appointmentId },
            fetchPolicy: 'network-only'
          });

          const claims = claimsResult.data?.insuranceClaimResultsByAppointment || [];
          
          if (claims.length > 0) {
            // Get policyId from first claim
            policyId = claims[0].policyId;
          } else {
            // If no existing claims, get the appointment to find the user and their policies
            try {
              const appointmentResult = await getAppointment({
                variables: { id: appointmentId },
                fetchPolicy: 'network-only'
              });

              const appointment = appointmentResult.data?.appointment;
              if (appointment?.app_user_id) {
                // Get active insurance policies for the user
                const policiesResult = await getPoliciesByUser({
                  variables: { appUserId: appointment.app_user_id },
                  fetchPolicy: 'network-only'
                });

                const policies = policiesResult.data?.insurancePoliciesByUser || [];
                // Find the first active policy
                const activePolicy = policies.find((p: any) => p.isActive);
                if (activePolicy) {
                  policyId = activePolicy.id;
                }
              }
            } catch (err) {
              // If we can't get appointment or policies, continue without insurance check
              console.warn('Failed to get appointment or policies for eligibility check:', err);
            }
          }
        } catch (err) {
          // If we can't get claims, continue without insurance check
          console.warn('Failed to get claims for eligibility check:', err);
        }
        
        // Always attempt eligibility check if we have a policyId
        if (policyId) {
          try {
            // Check eligibility for the selected category
            const eligibilityResult = await checkEligibility({
              variables: {
                policyId: policyId,
                category: values.category
              },
              fetchPolicy: 'network-only'
            });

            const eligibility = eligibilityResult.data?.insuranceEligibilityCheckByCategory;
            const coverageDetails = eligibility?.coverageDetails;
            const dollarCoverage = parseFloat(
              coverageDetails?.remainingCoverageAmount?.[values.category] || '0'
            );
            const hasFreeConsultQuota =
              values.category === 'CONSULTATION' &&
              (
                (coverageDetails?.freeConsultations != null && coverageDetails.freeConsultations > 0) ||
                (coverageDetails?.freeConsultationsPerYear != null && coverageDetails.freeConsultationsPerYear > 0) ||
                (coverageDetails?.freeConsultationsPerMonth != null && coverageDetails.freeConsultationsPerMonth > 0) ||
                (coverageDetails?.freeConsultationsPerWeek != null && coverageDetails.freeConsultationsPerWeek > 0)
              );

            // Free-consult quotas (e.g. staff plan 10/year) cover the consultation fee
            // minus any copayment. Dollar coverage pools cover up to their balance.
            const transactionAmount = parseFloat(values.amount) || 0;
            const copaymentAmount = eligibility?.copaymentAmount ?? 0;
            const remainingAmount = hasFreeConsultQuota
              ? Math.max(0, transactionAmount - copaymentAmount)
              : dollarCoverage;

            if (eligibility && remainingAmount > 0) {
                if (remainingAmount >= transactionAmount) {
                  // Full coverage - ask user if they want to claim insurance
                  const coverageMessage = (t('appointments.transactions.insuranceCoverageAvailable') || 
                    'You have insurance coverage available (${amount}). Would you like to claim insurance for this transaction?')
                    .replace('${amount}', `$${remainingAmount.toFixed(2)}`);
                  
                  Modal.confirm({
                    title: t('appointments.transactions.claimInsurance') || 'Claim Insurance?',
                    icon: <ExclamationCircleOutlined />,
                    content: coverageMessage,
                    okText: t('appointments.transactions.yes') || 'Yes',
                    cancelText: t('appointments.transactions.continueWithoutInsurance') || 'Continue Without Insurance',
                    onOk: async () => {
                      try {
                        // Create transaction with full_insurance_coverage
                        // original_amount = full transaction amount, amount = 0
                        const transactionResult = await createTransaction({
                          variables: {
                            createTransactionDto: {
                              ...transactionData,
                              original_amount: transactionAmount,
                              amount: 0,
                              payment_method: 'full_insurance_coverage',
                              status: 'completed'
                            }
                          }
                        });

                        const transactionId = transactionResult.data.createTransaction.id;

                        // Add claim items to existing claim or create new one
                        const claimItems = [{
                          name: transactionData.description || transactionData.category,
                          category: transactionData.category,
                          amount: transactionAmount.toString(),
                          approvedAmount: transactionAmount.toString(),
                          transactionId: transactionId
                        }];

                        if (policyId) {
                          await addClaimItems(policyId, claimItems);
                        }

                        message.success(t('appointments.transactions.transactionCreated'));
                        form.resetFields();
                        onRefetch?.();
                        onSuccess?.(transactionResult.data.createTransaction);
                        onCancel();
                      } catch (err: any) {
                        message.error(err?.message || t('appointments.transactions.transactionCreationFailed'));
                      }
                    },
                    onCancel: () => {
                      // Continue with normal transaction creation
                      proceedWithNormalTransaction(transactionData, values);
                    }
                  });
                  return;
                } else {
                  // Partial coverage - ask user if they want to claim insurance for the remaining balance
                  const uncoveredAmount = transactionAmount - remainingAmount;
                  const coverageMessage = (t('appointments.transactions.insuranceCoverageAvailable') || 
                    'You have insurance coverage available (${amount}). Would you like to claim insurance for this transaction?')
                    .replace('${amount}', `$${remainingAmount.toFixed(2)}`);
                  const partialMessage = (t('appointments.transactions.partialCoverageMessage') || 
                    'The remaining amount of ${amount} will be charged.')
                    .replace('${amount}', `$${uncoveredAmount.toFixed(2)}`);
                  
                  Modal.confirm({
                    title: t('appointments.transactions.claimInsurance') || 'Claim Insurance?',
                    icon: <ExclamationCircleOutlined />,
                    content: `${coverageMessage} ${partialMessage}`,
                    okText: t('appointments.transactions.yes') || 'Yes',
                    cancelText: t('appointments.transactions.continueWithoutInsurance') || 'Continue Without Insurance',
                    onOk: async () => {
                      try {
                        // For partial coverage:
                        // original_amount = full transaction amount
                        // amount = uncovered portion (what patient actually pays)
                        transactionData.original_amount = transactionAmount;
                        transactionData.amount = uncoveredAmount;
                        transactionData.payment_method = 'qfpay_checkout';
                        transactionData.metadata = {
                          qfpay: buildQfpayMetadata(appointmentId),
                        };
                        
                        // Create transaction for the uncovered amount
                        const transactionResult = await createTransaction({
                          variables: {
                            createTransactionDto: transactionData
                          }
                        });

                        const transactionId = transactionResult.data.createTransaction.id;

                        // Add claim items to existing claim or create new one for the remaining balance
                        const claimItems = [{
                          name: transactionData.description || transactionData.category,
                          category: transactionData.category,
                          amount: remainingAmount.toString(),
                          approvedAmount: remainingAmount.toString(),
                          transactionId: transactionId
                        }];

                        if (policyId) {
                          await addClaimItems(policyId, claimItems);
                        }

                        message.success(t('appointments.transactions.transactionCreated'));
                        form.resetFields();
                        onRefetch?.();
                        onSuccess?.(transactionResult.data.createTransaction);
                        onCancel();
                      } catch (err: any) {
                        message.error(err?.message || t('appointments.transactions.transactionCreationFailed'));
                      }
                    },
                    onCancel: () => {
                      // Continue with normal transaction creation (full amount)
                      proceedWithNormalTransaction(transactionData, values);
                    }
                  });
                  return;
                }
            }
          } catch (err) {
            // If eligibility check fails, continue with normal transaction creation
            console.warn('Eligibility check failed:', err);
          }
        }
      }

      // Normal transaction creation (no insurance or no coverage)
      await proceedWithNormalTransaction(transactionData, values);
    } catch (err: any) {
      message.error(err?.message || t('appointments.transactions.transactionCreationFailed'));
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title={t('appointments.transactions.createTransaction')}
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="amount"
              label={t('appointments.transactions.amount')}
              rules={[
                { required: true, message: t('appointments.transactions.amountRequired') },
                { type: 'number', min: 0, message: t('appointments.transactions.amountMustBePositive') }
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="0.00"
                precision={2}
                prefix="$"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="payment_method"
              label={t('appointments.transactions.paymentMethod')}
            >
              <Select
                placeholder={t('appointments.transactions.selectPaymentMethod')}
                allowClear
              >
                {/* <Select.Option value="development">{t('appointments.transactions.paymentMethods.development')}</Select.Option> */}
                <Select.Option value="qfpay_checkout">{t('appointments.transactions.paymentMethods.qfpay_checkout')}</Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="description"
          label={t('appointments.transactions.description')}
        >
          <Input placeholder={t('appointments.transactions.enterDescription')} />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="category"
              label={t('appointments.transactions.category')}
              rules={[
                { required: true, message: t('appointments.transactions.categoryRequired') }
              ]}
            >
              <Select
                placeholder={t('appointments.transactions.selectCategory')}
              >
                <Select.Option value="CONSULTATION">{t('appointments.transactions.categories.CONSULTATION')}</Select.Option>
                <Select.Option value="MEDICATION">{t('appointments.transactions.categories.MEDICATION')}</Select.Option>
                <Select.Option value="LAB_TEST">{t('appointments.transactions.categories.LAB_TEST')}</Select.Option>
                <Select.Option value="DIAGNOSTIC_TEST">{t('appointments.transactions.categories.DIAGNOSTIC_TEST')}</Select.Option>
                <Select.Option value="SURGERY">{t('appointments.transactions.categories.SURGERY')}</Select.Option>
                <Select.Option value="DELIVERY">{t('appointments.transactions.categories.DELIVERY')}</Select.Option>
                <Select.Option value="OTHER">{t('appointments.transactions.categories.OTHER')}</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="subcategory"
              label={t('appointments.transactions.subcategory')}
            >
              <Input placeholder={t('appointments.transactions.enterSubcategory')} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="notes"
          label={t('appointments.transactions.notes')}
        >
          <TextArea
            rows={3}
            placeholder={t('appointments.transactions.enterNotes')}
          />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              loading={creating}
            >
              {t('appointments.transactions.create')}
            </Button>
            <Button onClick={handleCancel}>
              {t('appointments.transactions.cancel')}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateTransactionDialog;

